import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, ArrowsClockwise, PaperPlaneTilt, Copy, Pencil, Check,
  Warning, Info, Lightbulb, ArrowRight, Trophy, XCircle, ChatCircleText,
  Envelope, InstagramLogo, WhatsappLogo, Globe, Phone as PhoneIcon,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { logDev } from "@/lib/logger";

// ---------------------------------------------------------------------------
// Next io — Conversion Panel (V1)
//
// Renders inside LeadDetail. Answers:
//   * Why this opportunity matters (qualification + reasoning)
//   * What sales angle to use
//   * Which channel + supporting asset
//   * The message draft (channel-aware) with Approve / Edit / Alternative
//   * The reply-analysis input + last analysis output
//   * Final outcome (Converted / Lost)
//
// Never sends outbound communication itself. Approval = user copies and
// sends externally; we log it and advance the stage.
// ---------------------------------------------------------------------------

const CHANNEL_META = {
  instagram_dm: { label: { tr: "Instagram mesajı", en: "Instagram DM" }, icon: InstagramLogo, tone: "text-pink-600 dark:text-pink-400" },
  whatsapp:     { label: { tr: "WhatsApp", en: "WhatsApp" }, icon: WhatsappLogo, tone: "text-emerald-600 dark:text-emerald-400" },
  email:        { label: { tr: "E-posta", en: "Email" }, icon: Envelope,  tone: "text-blue-600 dark:text-blue-400" },
  website_form: { label: { tr: "İletişim formu", en: "Contact form" }, icon: Globe, tone: "text-teal-600 dark:text-teal-400" },
  phone:        { label: { tr: "Telefon görüşmesi", en: "Phone call" }, icon: PhoneIcon, tone: "text-primary" },
};

// ---------------------------------------------------------------------------
// Safety helpers — defend against unexpected LLM output shapes so a
// malformed conversion doc can never crash the panel (and therefore never
// white-screen LeadDetail).
// ---------------------------------------------------------------------------
const isSafeHttpUrl = (u) => {
  if (typeof u !== "string") return false;
  const s = u.trim().toLowerCase();
  return s.startsWith("http://") || s.startsWith("https://");
};

const safeChannelKey = (k) => (typeof k === "string" && CHANNEL_META[k] ? k : null);

const channelLabel = (key, locale) => {
  const meta = CHANNEL_META[safeChannelKey(key)];
  if (!meta) return safeStr(key);
  return typeof meta.label === "string" ? meta.label : meta.label[locale === "en" ? "en" : "tr"];
};

const safeStr = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return ""; }
};

const STAGE_LABEL = {
  new:               { tr: "Yeni",               en: "New" },
  ready_to_contact:  { tr: "İletişime hazır",    en: "Ready to contact" },
  contacted:         { tr: "İletişim kuruldu",   en: "Contacted" },
  in_conversation:   { tr: "Görüşme sürüyor",    en: "In conversation" },
  converted:         { tr: "Kazanıldı",          en: "Converted" },
  lost:              { tr: "Kaybedildi",         en: "Lost" },
};

const POTENTIAL_META = {
  high:   { tr: "Yüksek", en: "High",   cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  medium: { tr: "Orta",   en: "Medium", cls: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  low:    { tr: "Düşük",  en: "Low",    cls: "border-muted-foreground/30 bg-muted/40 text-muted-foreground" },
};

export default function ConversionPanel({ lead, locale = "tr", onLeadUpdate }) {
  const t = (tr, en) => (locale === "en" ? en : tr);
  const conv = lead.conversion || {};
  const hasStrategy = !!conv.sales_angle;

  const [gen, setGen] = useState(false);        // strategy generating
  const [msgGen, setMsgGen] = useState(false);  // message drafting
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(() => safeChannelKey(conv.channel?.best));

  const applyLead = useCallback((next) => onLeadUpdate?.({ ...lead, ...next }), [lead, onLeadUpdate]);

  const CTRL_ERR = t("Bu işlem şu anda tamamlanamadı. Lütfen tekrar deneyin.", "This action could not be completed. Please try again.");

  const genStrategy = async () => {
    setGen(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/conversion/strategy`);
      const nextConv = (data && data.conversion) ? data.conversion : null;
      // If the server returned an unexpected shape, keep the previous
      // conversion state intact instead of clobbering it with something
      // that could crash the render tree.
      if (nextConv) {
        applyLead({ conversion: nextConv });
        setSelectedChannel(safeChannelKey(nextConv.channel?.best));
      }
      toast.success(t("Strateji hazırlandı", "Strategy ready"));
    } catch (e) {
      logDev("conv.strategy", e);
      toast.error(formatApiError(e.response?.data?.detail) || CTRL_ERR);
    } finally { setGen(false); }
  };

  const genMessage = async (opts = {}) => {
    setMsgGen(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/conversion/message`, {
        channel: opts.channel || selectedChannel || safeChannelKey(conv.channel?.best),
        alternative: !!opts.alternative,
      });
      const draft = (data && data.draft && typeof data.draft === "object") ? data.draft : null;
      if (draft) {
        applyLead({ conversion: { ...conv, current_draft: draft } });
      }
      setEditing(false);
      toast.success(opts.alternative ? t("Alternatif hazırlandı", "Alternative ready") : t("Mesaj hazır", "Message ready"));
    } catch (e) {
      logDev("conv.message", e);
      toast.error(formatApiError(e.response?.data?.detail) || CTRL_ERR);
    } finally { setMsgGen(false); }
  };

  const approveDraft = async () => {
    const draft = editing ? { ...(conv.current_draft || {}), body: editBody, subject: editSubject } : conv.current_draft;
    if (!draft?.body) return;
    try {
      try {
        // Clipboard API can throw synchronously in some browsers (permission,
        // insecure context). Never let this take down the page.
        const clip = navigator.clipboard;
        if (clip && typeof clip.writeText === "function") {
          await clip.writeText(
            draft.subject ? `Subject: ${draft.subject}\n\n${draft.body}` : draft.body
          );
        }
      } catch (clipErr) {
        logDev("conv.clipboard", clipErr);
      }
      const { data } = await api.post(`/leads/${lead.id}/conversion/approve`, {
        channel: draft.channel || (conv.channel || {}).best,
        body: draft.body,
        subject: draft.subject || null,
      });
      if (data && data.conversion) applyLead({ conversion: data.conversion });
      toast.success(t("Mesaj kopyalandı ve gönderildi olarak işaretlendi", "Copied — marked as sent"));
    } catch (e) {
      logDev("conv.approve", e);
      toast.error(CTRL_ERR);
    }
  };

  const submitReply = async () => {
    const rt = reply.trim();
    if (!rt) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/conversion/reply`, { reply_text: rt });
      if (data && data.conversion) applyLead({ conversion: data.conversion });
      setReply("");
      toast.success(t("Cevap analiz edildi", "Reply analyzed"));
    } catch (e) {
      logDev("conv.reply", e);
      toast.error(formatApiError(e.response?.data?.detail) || CTRL_ERR);
    } finally { setReplying(false); }
  };

  const markOutcome = async (result) => {
    const note = result === "lost"
      ? (prompt(t("Kısa not (opsiyonel)", "Short note (optional)")) || null)
      : null;
    const valueStr = result === "converted"
      ? prompt(t("Anlaşma değeri (opsiyonel)", "Deal value (optional)"))
      : null;
    // parseFloat("") = NaN, parseFloat(null) = NaN — coerce to null so JSON
    // stays clean and no NaN reaches the render tree.
    let value = null;
    if (valueStr) {
      const n = parseFloat(valueStr);
      value = Number.isFinite(n) ? n : null;
    }
    try {
      const { data } = await api.post(`/leads/${lead.id}/conversion/outcome`, { result, value, note });
      applyLead({ conversion: data.conversion });
      toast.success(result === "converted"
        ? t("Kazanıldı olarak işaretlendi", "Marked as converted")
        : t("Kaybedildi olarak işaretlendi", "Marked as lost"));
    } catch (e) {
      logDev("conv.outcome", e);
      toast.error(t("Bu işlem şu anda tamamlanamadı. Lütfen tekrar deneyin.", "This action could not be completed. Please try again."));
    }
  };

  const startEdit = () => {
    setEditBody(conv.current_draft?.body || "");
    setEditSubject(conv.current_draft?.subject || "");
    setEditing(true);
  };

  // -------- Rendering ------------------------------------------------------
  const stage = conv.stage || "new";
  const stageLabel = STAGE_LABEL[stage] || STAGE_LABEL.new;
  const potential = conv.conversion_potential;
  const potMeta = POTENTIAL_META[potential];
  const available = (conv.channel?.available) || [];
  const activeChannel = selectedChannel && available.includes(selectedChannel)
    ? selectedChannel
    : safeChannelKey(conv.channel?.best);

  return (
    <section data-testid="conversion-panel" className="space-y-5">
      {/* ---------- Header row ---------- */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-accent/10 text-accent grid place-items-center">
            <Sparkle size={16} weight="fill" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">{t("Dönüşüm Motoru", "Conversion Engine")}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
              {t("Fırsatı satışa nasıl yaklaştıralım?", "How do we move this closer to a sale?")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="conv-stage" className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-border/60 bg-secondary font-semibold">
            {locale === "en" ? stageLabel.en : stageLabel.tr}
          </span>
          {potMeta && (
            <span data-testid="conv-potential" className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold ${potMeta.cls}`}>
              {t("Potansiyel", "Potential")}: {locale === "en" ? potMeta.en : potMeta.tr}
            </span>
          )}
        </div>
      </div>

      {/* ---------- Empty state: no strategy yet ---------- */}
      {!hasStrategy && stage !== "converted" && stage !== "lost" && (
        <div className="rounded-lg border-2 border-dashed border-accent/30 bg-accent/[0.03] p-6 text-center" data-testid="conv-empty">
          <div className="w-11 h-11 rounded-full bg-accent/10 text-accent grid place-items-center mx-auto mb-3">
            <Lightbulb size={18} weight="duotone" />
          </div>
          <div className="font-semibold text-sm mb-1">{t("Ticari strateji henüz üretilmedi.", "No commercial strategy yet.")}</div>
          <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
            {t(
              "Next io bu fırsatı ticari olarak değerlendirsin: neden değerli, hangi yaklaşım en uygun, hangi kanal, hangi ilk mesaj?",
              "Let Next io evaluate this opportunity commercially: why it matters, what angle fits, which channel, what first message."
            )}
          </p>
          <Button data-testid="conv-generate-strategy" onClick={genStrategy} disabled={gen} className="mt-4 gap-2">
            <Sparkle size={14} weight="fill" />
            {gen ? t("Analiz ediliyor…", "Analyzing…") : t("Strateji Üret", "Generate strategy")}
          </Button>
        </div>
      )}

      {/* ---------- Strategy present: qualification + reasoning ---------- */}
      {hasStrategy && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="conv-reasoning">
          <Card icon={Info} title={t("Neden Bu Fırsat?", "Why this lead?")} testid="conv-why-lead" body={safeStr(conv.qualification?.why_lead)} />
          <Card icon={Info} title={t("Neden Bu Ürün / Teklif?", "Why this offer?")} testid="conv-why-offer" body={safeStr(conv.qualification?.why_offer)} />
          <Card icon={Info} title={t("Neden Şimdi?", "Why now?")} testid="conv-why-now" body={safeStr(conv.qualification?.why_now)} />
          <Card
            icon={Warning}
            title={t("Kanıt Gücü", "Evidence strength")}
            testid="conv-evidence"
            body={
              (conv.qualification?.evidence_strength === "weak"
                ? t("Elimizde sınırlı kanıt var. Uydurma yapmayın; ilk mesajı düşük baskılı tutun.", "Evidence is thin. Don't fabricate; keep the first message low-pressure.")
                : conv.qualification?.evidence_strength === "moderate"
                ? t("Orta düzey kanıt.", "Moderate evidence.")
                : conv.qualification?.evidence_strength === "strong"
                ? t("Güçlü kanıt.", "Strong evidence.")
                : "")
              + (conv.qualification?.notes ? " — " + safeStr(conv.qualification.notes) : "")
            }
          />
        </div>
      )}

      {/* ---------- Sales angle + Channel + Asset ---------- */}
      {hasStrategy && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="conv-strategy">
          <StrategyCard
            icon={Lightbulb} title={t("Satış Yaklaşımı", "Sales angle")} testid="conv-angle"
            primary={safeStr(conv.sales_angle?.name)}
            secondary={safeStr(conv.sales_angle?.reason)}
            highlight={safeStr(conv.sales_angle?.primary_goal)}
            highlightLabel={t("Ana hedef", "Primary goal")}
          />
          <StrategyCard
            icon={((CHANNEL_META[safeChannelKey(conv.channel?.best)]) || {}).icon || ChatCircleText}
            title={t("Kanal", "Channel")} testid="conv-channel"
            primary={channelLabel(conv.channel?.best, locale)}
            secondary={safeStr(conv.channel?.reason)}
            highlight={safeChannelKey(conv.channel?.secondary) && (
              (t("Yedek", "Fallback") + ": " + channelLabel(conv.channel.secondary, locale))
            )}
          />
          <StrategyCard
            icon={conv.supporting_asset?.type === "missing" ? Warning : Globe}
            title={t("Destekleyici Öğe", "Supporting asset")} testid="conv-asset"
            primary={conv.supporting_asset?.type === "missing"
              ? t("Eksik", "Missing")
              : (conv.supporting_asset?.type === "none"
                  ? t("Gerekli değil", "Not needed")
                  : safeStr(conv.supporting_asset?.type))}
            secondary={safeStr(conv.supporting_asset?.reason)}
            highlight={isSafeHttpUrl(conv.supporting_asset?.url)
              ? (<a href={conv.supporting_asset.url} target="_blank" rel="noreferrer noopener" className="text-accent hover:underline break-all">{conv.supporting_asset.url}</a>)
              : (conv.supporting_asset?.url
                  ? (<span className="text-xs text-muted-foreground break-all">{safeStr(conv.supporting_asset.url)}</span>)
                  : (conv.supporting_asset?.needed ? (t("Öneri", "Suggestion") + ": " + safeStr(conv.supporting_asset.needed)) : null))}
          />
        </div>
      )}

      {/* ---------- Next best action ---------- */}
      {hasStrategy && conv.next_action?.label && stage !== "converted" && stage !== "lost" && (
        <div className="rounded-lg border border-accent/30 bg-accent/[0.03] p-4" data-testid="conv-next-action">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-accent/10 text-accent grid place-items-center flex-shrink-0">
              <ArrowRight size={16} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="acas-eyebrow flex items-center gap-2">
                {t("Sıradaki Adım", "Next best action")}
                {conv.next_action.priority && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${
                    conv.next_action.priority === "high" ? "bg-accent/15 text-accent" :
                    conv.next_action.priority === "medium" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" :
                    "bg-muted text-muted-foreground"
                  }`}>{conv.next_action.priority}</span>
                )}
              </div>
              <div className="font-semibold mt-0.5">{safeStr(conv.next_action.label)}</div>
              {conv.next_action.why && (
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{safeStr(conv.next_action.why)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Message draft ---------- */}
      {hasStrategy && stage !== "converted" && stage !== "lost" && (
        <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="conv-draft">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="acas-eyebrow">{t("Mesaj Taslağı", "Message draft")}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {available.map((ch) => {
                const M = CHANNEL_META[ch];
                if (!M) return null;
                const active = activeChannel === ch;
                const Icon = M.icon;
                return (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setSelectedChannel(ch)}
                    disabled={msgGen}
                    data-testid={`conv-channel-${ch}`}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 transition ${
                      active ? "border-accent bg-accent/10 text-accent" : "border-border/60 hover:border-accent/40"
                    }`}
                  >
                    <Icon size={13} weight={active ? "fill" : "regular"} />
                    {channelLabel(ch, locale)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={conv.current_draft?.body ? "flex justify-end border-b border-border/50 pb-3 mb-3" : "text-center py-6"}>
            <Button data-testid="conv-generate-message" onClick={() => genMessage({ channel: activeChannel })} disabled={msgGen || !activeChannel} className="gap-2">
                <PaperPlaneTilt size={14} />
                {msgGen ? t("Yazılıyor…", "Drafting…") : t("Mesaj Hazırla", "Generate message")}
            </Button>
          </div>

          {conv.current_draft?.body && (
            <div className="space-y-3">
              {conv.current_draft.subject && !editing && (
                <div className="rounded-md bg-secondary/60 border border-border/60 p-3 text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{t("Konu", "Subject")}: </span>
                  <span className="font-semibold">{safeStr(conv.current_draft.subject)}</span>
                </div>
              )}
              {editing && conv.current_draft.channel === "email" && (
                <Input
                  data-testid="conv-edit-subject"
                  value={editSubject} onChange={(e) => setEditSubject(e.target.value)}
                  placeholder={t("Konu", "Subject")} className="text-sm"
                />
              )}
              {editing ? (
                <Textarea
                  data-testid="conv-edit-body"
                  value={editBody} onChange={(e) => setEditBody(e.target.value)}
                  rows={8} className="text-sm leading-relaxed"
                />
              ) : (
                <div data-testid="conv-draft-body" className="rounded-md border border-border/60 bg-secondary/40 p-4 text-sm leading-relaxed whitespace-pre-wrap">
                  {safeStr(conv.current_draft.body)}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap pt-2">
                <Button data-testid="conv-approve" onClick={approveDraft} className="gap-1.5">
                  <Copy size={13} weight="bold" /> {t("Onayla & Kopyala", "Approve & copy")}
                </Button>
                <Button data-testid="conv-edit" variant="outline" onClick={editing ? () => setEditing(false) : startEdit} className="gap-1.5">
                  <Pencil size={13} /> {editing ? t("Bitir", "Done") : t("Düzenle", "Edit")}
                </Button>
                <Button data-testid="conv-regenerate" variant="ghost" onClick={() => genMessage({ channel: activeChannel, alternative: true })} disabled={msgGen || !activeChannel} className="gap-1.5">
                  <ArrowsClockwise size={13} className={msgGen ? "animate-spin" : ""} />
                  {t("Alternatif", "Alternative")}
                </Button>
                {hasStrategy && (
                  <Button variant="ghost" size="sm" onClick={genStrategy} disabled={gen} className="gap-1.5 ml-auto text-xs">
                    <Sparkle size={12} /> {t("Stratejiyi yenile", "Refresh strategy")}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground pt-1 leading-relaxed">
                {t(
                  "Next io hiçbir mesajı otomatik göndermez. Onayladığınızda taslağı panonuza kopyalar ve durumu güncelleriz — gönderimi siz yaparsınız.",
                  "Next io never sends anything automatically. Approval copies the draft to your clipboard and marks the stage as contacted — you send it yourself."
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---------- Incoming reply analyzer ---------- */}
      {stage !== "converted" && stage !== "lost" && (conv.stage === "contacted" || conv.stage === "in_conversation") && (
        <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="conv-reply-box">
          <div className="acas-eyebrow mb-2 flex items-center gap-1.5">
            <ChatCircleText size={11} weight="fill" />
            {t("Gelen Cevabı Yapıştır", "Paste the reply you received")}
          </div>
          <Textarea
            data-testid="conv-reply-input"
            value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder={t("Onlardan gelen mesajı buraya yapıştırın…", "Paste the reply from the lead here…")}
            rows={4} className="text-sm"
          />
          <div className="flex justify-end mt-2">
            <Button data-testid="conv-reply-submit" onClick={submitReply} disabled={replying || !reply.trim()} className="gap-1.5">
              <Sparkle size={13} weight="fill" /> {replying ? t("Analiz ediliyor…", "Analyzing…") : t("Analiz Et", "Analyze")}
            </Button>
          </div>
        </div>
      )}

      {/* ---------- Last reply analysis ---------- */}
      {conv.last_interpretation && (
        <div className="rounded-lg border border-accent/30 bg-accent/[0.03] p-5" data-testid="conv-last-analysis">
          <div className="acas-eyebrow mb-2">{t("Son Analiz", "Last analysis")}</div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span data-testid="conv-intent" className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold ${
              conv.intent === "high" ? "border-accent/40 bg-accent/10 text-accent"
              : conv.intent === "medium" ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
              : "border-muted-foreground/30 bg-muted text-muted-foreground"
            }`}>
              {t("Alım niyeti", "Buying intent")}: {conv.intent || "—"}
            </span>
            {conv.objection && (
              <span data-testid="conv-objection" className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                {t("Engel", "Objection")}: {safeStr(conv.objection)}
              </span>
            )}
          </div>
          <p className="text-sm leading-relaxed">{safeStr(conv.last_interpretation)}</p>
          {conv.strategy_notes && (
            <div className="mt-3 text-xs text-muted-foreground italic border-l-2 border-accent/40 pl-3">
              {t("Strateji güncellemesi", "Strategy update")}: {safeStr(conv.strategy_notes)}
            </div>
          )}
        </div>
      )}

      {/* ---------- Outcome ---------- */}
      {stage !== "converted" && stage !== "lost" && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/60" data-testid="conv-outcome-actions">
          <Button variant="outline" size="sm" onClick={() => markOutcome("lost")} data-testid="conv-mark-lost" className="gap-1.5">
            <XCircle size={13} /> {t("Kaybedildi", "Mark lost")}
          </Button>
          <Button size="sm" onClick={() => markOutcome("converted")} data-testid="conv-mark-converted" className="gap-1.5">
            <Trophy size={13} weight="fill" /> {t("Kazanıldı", "Mark converted")}
          </Button>
        </div>
      )}
      {(stage === "converted" || stage === "lost") && (
        <div className={`rounded-lg border p-4 ${stage === "converted" ? "border-emerald-500/30 bg-emerald-500/5" : "border-muted-foreground/20 bg-muted/40"}`} data-testid="conv-outcome-badge">
          <div className="flex items-center gap-2">
            {stage === "converted" ? <Trophy size={16} weight="fill" className="text-emerald-600 dark:text-emerald-400" /> : <XCircle size={16} className="text-muted-foreground" />}
            <div>
              <div className="text-sm font-semibold">
                {stage === "converted" ? t("Bu fırsat kazanıldı", "Opportunity won") : t("Bu fırsat kaybedildi", "Opportunity closed as lost")}
                {Number.isFinite(conv.outcome?.value) ? ` — ${conv.outcome.value}` : ""}
              </div>
              {conv.outcome?.note && (
                <div className="text-xs text-muted-foreground mt-0.5">{safeStr(conv.outcome.note)}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Small cards.
// ---------------------------------------------------------------------------
function Card({ icon: Icon, title, body, testid }) {
  const text = safeStr(body);
  if (!text) return null;
  return (
    <div className="rounded-lg border border-border/60 bg-card p-4" data-testid={testid}>
      <div className="acas-eyebrow mb-1.5 flex items-center gap-1.5">
        <Icon size={11} weight="fill" /> {title}
      </div>
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function StrategyCard({ icon: Icon, title, testid, primary, secondary, highlight, highlightLabel }) {
  const primaryText   = safeStr(primary);
  const secondaryText = safeStr(secondary);
  // `highlight` can legitimately be a ReactNode (e.g. an <a>). Only coerce
  // if it's a plain non-string primitive that would render awkwardly.
  const isNode = React.isValidElement(highlight);
  const highlightNode = isNode ? highlight : safeStr(highlight);
  return (
    <div className="rounded-lg border border-border/60 bg-card p-4" data-testid={testid}>
      <div className="acas-eyebrow mb-2 flex items-center gap-1.5">
        <Icon size={11} weight="fill" /> {title}
      </div>
      <div className="font-semibold text-sm">{primaryText || "—"}</div>
      {secondaryText && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{secondaryText}</div>}
      {highlightNode && (
        <div className="mt-3 rounded-md bg-secondary/60 border border-border/60 p-2.5">
          {highlightLabel && <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">{highlightLabel}</div>}
          <div className="text-xs leading-relaxed">{highlightNode}</div>
        </div>
      )}
    </div>
  );
}
